import { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ACCENT } from './offerteUi';

/**
 * Het pre-flight-vel vóór "Naar Gripp": één overzicht van klant, regels,
 * verplichte velden, prijscheck en de AI-check — in plaats van vier losse
 * alerts die je naar boven sturen. De AI-check staat hier bewust prominent:
 * die vangt fouten die anders bij de klant belanden. Doorzetten mag ook met
 * open punten (de planner beslist), maar nooit zonder ze gezien te hebben.
 */
const Rij = ({ status, titel, detail, actie }) => {
  const icoon = status === 'ok'
    ? <CheckCircleIcon sx={{ fontSize: 19, color: '#059669' }} />
    : status === 'let-op'
      ? <ErrorIcon sx={{ fontSize: 19, color: '#d97706' }} />
      : <RadioButtonUncheckedIcon sx={{ fontSize: 19, color: '#94a3b8' }} />;
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start"
      sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
      <Box sx={{ mt: 0.1 }}>{icoon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700}>{titel}</Typography>
        {detail && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{detail}</Typography>}
      </Box>
      {actie}
    </Stack>
  );
};

function OffertePreflight({
  open, onClose, onConfirm, actionLabel, busy,
  company, isExistingOffer, editingOffer,
  linesCount, total, missingFields = [], klantMissing = false,
  priceCheckWarnings = 0, priceCheck = [], lines = [], englishGaps = 0, language = 'nl',
  checkResult, onRunCheck, checking = false,
}) {
  const checks = checkResult?.checks || [];
  const issues = checks.filter((c) => String(c.status || '').toLowerCase() !== 'ok');
  const [prijsOpen, setPrijsOpen] = useState(false);
  // De afwijkende regels mét regel-info erbij (priceCheck loopt parallel aan lines).
  const prijsAfwijkingen = priceCheck
    .map((pc, i) => ({ pc, line: lines[i] }))
    .filter(({ pc }) => pc?.level === 'warn' || pc?.level === 'check');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Klaar voor Gripp?
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
          Eén blik: wat staat er goed, wat verdient nog aandacht. Doorzetten kan altijd — maar niet ongezien.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Rij
          status={isExistingOffer || company?.id ? 'ok' : 'let-op'}
          titel="Klant"
          detail={isExistingOffer
            ? `Bestaande offerte${editingOffer?.number ? ` ${editingOffer.number}` : ''} — klant staat in Gripp.`
            : company?.name || 'Nog geen klant gekozen — kies er een in het eigenschappen-paneel.'}
        />
        <Rij
          status={linesCount > 0 ? 'ok' : 'let-op'}
          titel="Offerteregels"
          detail={linesCount > 0
            ? `${linesCount} ${linesCount === 1 ? 'regel' : 'regels'} · totaal € ${Number(total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })} excl. btw`
            : 'Geen regels — er gaat alleen een omschrijving naar Gripp.'}
        />
        <Rij
          status={missingFields.length === 0 && !klantMissing ? 'ok' : 'let-op'}
          titel="Verplichte Gripp-velden"
          detail={missingFields.length === 0
            ? 'Doel, dienst, omvang en projecttype zijn gevuld.'
            : `Nog leeg: ${missingFields.join(', ')} — in Gripp moet je die anders alsnog invullen.`}
        />
        <Rij
          status={priceCheckWarnings === 0 ? 'ok' : 'let-op'}
          titel="Prijscheck"
          detail={priceCheckWarnings === 0
            ? 'Geen afwijkingen van eerdere prijzen gevonden.'
            : `${priceCheckWarnings} ${priceCheckWarnings === 1 ? 'regel wijkt' : 'regels wijken'} af van wat we eerder rekenden.`}
          actie={prijsAfwijkingen.length > 0 ? (
            <Button size="small" variant="outlined" onClick={() => setPrijsOpen((v) => !v)}
              sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0, color: '#b45309', borderColor: '#fde68a' }}>
              {prijsOpen ? 'Verberg' : 'Bekijk'}
            </Button>
          ) : null}
        />
        <Collapse in={prijsOpen && prijsAfwijkingen.length > 0} unmountOnExit>
          <Box sx={{ my: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
            {prijsAfwijkingen.map(({ pc, line }, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="baseline"
                sx={{ py: 0.5, borderBottom: i < prijsAfwijkingen.length - 1 ? '1px solid #fde68a' : 'none' }}>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0, color: '#78350f', fontWeight: 600 }} noWrap>
                  {line?.description || `Regel ${i + 1}`}
                </Typography>
                <Typography variant="caption" sx={{ color: '#78350f', flexShrink: 0 }}>
                  nu €{Number(line?.sellingprice || 0).toLocaleString('nl-NL')}
                </Typography>
                <Typography variant="caption" sx={{ color: pc.level === 'warn' ? '#dc2626' : '#d97706', flexShrink: 0, fontWeight: 700 }}>
                  eerder ~€{pc.median} (n={pc.count}){pc.deviationPct ? `, ${pc.deviationPct > 0 ? '+' : ''}${pc.deviationPct}%` : ''}
                </Typography>
              </Stack>
            ))}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#92400e' }}>
              Afwijken mag — dit is wat we eerder voor dezelfde regel rekenden, aanpassen doe je in de regeltabel.
            </Typography>
          </Box>
        </Collapse>
        {language === 'en' && (
          <Rij
            status={englishGaps === 0 ? 'ok' : 'let-op'}
            titel="Engelse producten"
            detail={englishGaps === 0
              ? 'Alle regels hebben een Engels product.'
              : `${englishGaps} ${englishGaps === 1 ? 'regel heeft' : 'regels hebben'} nog geen Engelse productkoppeling.`}
          />
        )}
        <Rij
          status={checkResult ? (issues.length === 0 ? 'ok' : 'let-op') : 'leeg'}
          titel="AI-offertecheck"
          detail={checkResult
            ? (issues.length === 0
              ? `Alle ${checks.length} controles akkoord.`
              : `${issues.length} van ${checks.length} controles ${issues.length === 1 ? 'vraagt' : 'vragen'} aandacht:\n${issues.slice(0, 3).map((c) => `• ${c.titel}`).join('\n')}${issues.length > 3 ? '\n…' : ''}`)
            : 'Nog niet gedraaid — de check vergelijkt met de bron, het huistarief, btw en eerdere offertes.'}
          actie={(
            <Button size="small" variant="outlined" onClick={onRunCheck} disabled={checking}
              startIcon={checking ? <CircularProgress size={13} /> : <FactCheckIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0, color: '#b45309', borderColor: '#fde68a' }}>
              {checking ? 'Bezig…' : checkResult ? 'Opnieuw' : 'Controleer'}
            </Button>
          )}
        />
        {checkResult?.samenvatting && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#475569', whiteSpace: 'pre-wrap' }}>
            {checkResult.samenvatting}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Chip size="small" label="Sluiten kan altijd — er is nog niets verstuurd"
          sx={{ mr: 'auto', height: 22, fontSize: 11, bgcolor: '#f1f5f9', color: '#64748b' }} />
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}>Terug</Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}
          startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }}>
          {busy ? 'Bezig…' : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OffertePreflight;
